import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default {
	name: "TableModule",
	data() {
		return {
			columns: [],
			rows: [],
			rowsData: [],
			pointerY: 0,
			mouseY: 0,
			height: 0,
			interval: null,
			slide: 0,
			numberOfSlides: 1,
			isMobile: false,
		};
	},
	computed: {
		isPrevSlide() {
			return this.slide > 0;
		},
		isNextSlide() {
			return this.slide < this.columns.length - 2;
		},
	},
	methods: {
		onSwipe(ev) {
			if(ev.type === 'swipeleft') {
				this.nextSlide()
			} else {
				this.prevSlide()
			}
		},
		onMouseMove(ev) {
			const mouseY = ev.clientY - this.$el.getBoundingClientRect().y;

			const row = this.getClosesRow(mouseY);

			this.height = row.height;
			this.mouseY = row.top - this.height / 2;
		},
		setPointerPosition() {
			this.pointerY = this.lerp(this.pointerY, this.mouseY, 0.1);
		},
		lerp(start, end, amt) {
			return (1 - amt) * start + amt * end;
		},
		onMouseEnter() {
			this.height = 28;
			this.$el.addEventListener("mousemove", this.onMouseMove);
			this.interval = setInterval(this.setPointerPosition, 1000 / 60);
		},
		onMouseLeave() {
			this.height = 0;
			this.$el.removeEventListener("mousemove", this.onMouseMove);
			clearInterval(this.interval);
		},
		onResize() {
			this.isMobile = window.innerWidth < 580;

			if (this.isMobile) {
				this.setInitSlide();
			}

			this.buildRowsData();
			this.setTimelineAnimation();
			this.height = 0;
			this.$el.removeEventListener("mousemove", this.onMouseMove);
			clearInterval(this.interval);
		},
		getClosesRow(value) {
			return this.rowsData.sort((a, b) => {
				return Math.abs(a.top - value) - Math.abs(b.top - value);
			})[0];
		},
		buildRowsData() {
			this.rowsData = [];
			const parentTop = this.$el.getBoundingClientRect().y;

			this.rows.forEach((el) => {
				const boundingData = el.getBoundingClientRect();
				const elTop = boundingData.y - parentTop + boundingData.height / 2;
				this.rowsData.push({
					top: elTop,
					height: boundingData.height * 0.8,
				});
			});
		},
		nextSlide() {
			if (!this.isNextSlide) return false;

			this.slide++;
		},
		prevSlide() {
			if (!this.isPrevSlide) return false;

			this.slide--;
		},
		setInitSlide() {
			this.slide = 0;
			gsap.set(
				this.columns.filter((v, i) => [0, 1, 5].indexOf(i) === -1),
				{ autoAlpha: 0 }
			);
		},
		setSlide(newIndex, oldIndex) {
			const vector = newIndex > oldIndex ? -1 : 1;
			const hidingIndex = vector === -1 ? oldIndex : oldIndex + 1;
			const [old, first, second] = [this.columns[hidingIndex], this.columns[newIndex], this.columns[newIndex + 1]];
			gsap.to(old, { x: "+=" + vector * 20, autoAlpha: 0 });
			gsap.to([first, second], { x: -1 * newIndex * 50, autoAlpha: 1 });
		},
		setTimelineAnimation() {
			if (this.tl) {
				this.tl.clear(true);
			} else {
				this.tl = gsap.timeline({
					// yes, we can add it to an entire timeline!
					scrollTrigger: {
						trigger: this.$el,
						// markers: process.env.NODE_ENV !== "production",
						markers: false,
						start: "top bottom-=30%", // when the top of the trigger hits the top of the viewport
						end: "bottom bottom-=10%",
						// end: "top top", // end after scrolling 500px beyond the start
						scrub: 0.1, // smooth scrubbing, takes 1 second to "catch up" to the scrollbar
						snap: {
							snapTo: "labels", // snap to the closest label in the timeline
							duration: { min: 0.2, max: 3 }, // the snap animation should be at least 0.2 seconds, but no more than 3 seconds (determined by velocity)
							delay: 0.2, // wait 0.2 seconds from the last scroll event before doing the snapping
							ease: "power1.inOut", // the ease of the snap animation ("power3" by default)
						},
					},
				});
			}

			const firstColEl = this.$el.querySelectorAll(".table__column:first-child .label");
			const columns = Array.prototype.slice.call(this.$el.querySelectorAll(".table__column:not(:first-child)"));
			let animatedColumns = columns;
			if (this.isMobile) {
				animatedColumns = columns.filter((item, i) => {
					return [0, 1, 5].indexOf(i) !== -1;
				});
			}

			gsap.set(animatedColumns, { autoAlpha: 0 });

			this.tl
				.addLabel("start1")
				.fromTo(this.$refs.header, { y: 100, autoAlpha: 0 }, { y: 0, autoAlpha: 1 })
				.addLabel("columns")
				.fromTo(
					animatedColumns,
					{ x: 30, autoAlpha: 0 },
					{ x: 0, autoAlpha: 1, stagger: 0.1, onComplete: () => (this.isMobile ? this.setSlide(0, 1) : true) }
				)
				.addLabel("header")
				.fromTo(firstColEl, { y: 30, autoAlpha: 0 }, { y: 0, autoAlpha: 1, stagger: 0.1 }, "-=0.5")
				.addLabel("end");
		},
	},
	mounted() {
		this.isMobile = window.innerWidth < 580;
		this.columns = Array.prototype.slice.call(this.$el.querySelectorAll(".table__column.var_middle"));
		this.numberOfSlides = this.columns.length;
		this.rows = this.$el.querySelectorAll(".table__column:first-child>span");
		this.$refs.table.addEventListener("mouseenter", this.onMouseEnter);
		this.$refs.table.addEventListener("mouseleave", this.onMouseLeave);
		window.addEventListener("resize", this.onResize);

		this.buildRowsData();
		this.setInitSlide();
		this.setTimelineAnimation();
	},
	beforeDestroy() {
		this.$refs.table.removeEventListener("mousemove", this.onMouseMove);
		this.$refs.table.removeEventListener("mouseleave", this.onMouseLeave);
		window.removeEventListener("resize", this.onResize);
	},
	watch: {
		slide(newValue, oldValue) {
			this.setSlide(newValue, oldValue);
		},
	},
};
